import { Injectable, type Provider } from '@angular/core';

import { ContentEngagementService, type ReactionState } from './content-engagement.service';
import type { ReactionId } from '../reaction-glyph/reaction-glyph.component';

/**
 * SAVE AND REACT, WITHOUT A SESSION.
 *
 * Both mutations are viewer-scoped, so unauthenticated they 401 — and the real
 * service does the RIGHT thing with that: it flips optimistically and then
 * puts the tile back, because a bookmark that stays lit after a failed write
 * is a lie the reader will act on. Correct, and completely un-reviewable: in
 * the harness nothing ever appears to save.
 *
 * This keeps the optimistic half and skips the round trip. It overrides only
 * the two write methods; the override maps, the reconcile shape and every
 * reader are the real service's, inherited unchanged.
 */
@Injectable()
export class ContentEngagementPreviewService extends ContentEngagementService {
  override async toggleStar(
    contentId: string,
    currentlyStarred: boolean,
  ): Promise<boolean> {
    this.setStarPreview(contentId, !currentlyStarred);
    return true;
  }

  override async react(
    contentId: string,
    pressed: ReactionId,
    current: ReactionState,
  ): Promise<boolean> {
    // The same toggle the real path computes, for the same reason: pressing
    // your own reaction withdraws it, and the count follows the stance rather
    // than the press.
    const withdrawing = current.viewerReaction === pressed;
    // `?reactFail=1` refuses the press, so the failure path can be reviewed:
    // the press shows, then goes back, exactly as the real service does.
    if (new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('reactFail') === '1') {
      this.setReactionPreview(contentId, {
        viewerReaction: withdrawing ? null : pressed,
        count: current.count + (withdrawing ? -1 : current.viewerReaction ? 0 : 1),
      });
      await new Promise(resolve => setTimeout(resolve, 150));
      this.setReactionPreview(contentId, current);
      return false;
    }
    this.setReactionPreview(contentId, {
      viewerReaction: withdrawing ? null : pressed,
      count: current.count + (withdrawing ? -1 : current.viewerReaction ? 0 : 1),
    });
    return true;
  }

  /**
   * Answering a poll, without a server.
   *
   * ⚠ THIS ONE MOVES THE COUNT, and the real path deliberately does not. There,
   * the count is the server's and arrives in the mutation's reply; here there is
   * no reply, so leaving it alone would show the reader's tick against standings
   * that never budge — and the one thing worth reviewing on a poll is the bars
   * running out when you answer. Local by one, which is the truth: one more
   * person voted, and it was you.
   */
  override async votePoll(contentId: string, optionId: string): Promise<boolean> {
    const before = this.voteOverrides().get(contentId);
    const counts = { ...(before?.counts ?? {}) };
    counts[optionId] = (counts[optionId] ?? 0) + 1;
    this.setVotePreview(contentId, {
      votedOptionIds: [...(before?.votedOptionIds ?? []), optionId],
      counts,
    });
    return true;
  }
}

/** Drop into a route's `providers` to write locally instead of to the API. */
export const CONTENT_ENGAGEMENT_PREVIEW_PROVIDER: Provider = {
  provide: ContentEngagementService,
  useClass: ContentEngagementPreviewService,
};
